/**
 * Módulo Carousel - Exemplo de módulo funcional
 * Exibe um carrossel de banners consumindo dados do Strapi
 */

import BaseModule from './BaseModule.js';
import StrapiService from '../services/StrapiService.js';
import { transformBanner } from '../services/transformers.js';

class CarouselModule extends BaseModule {
  constructor(config) {
    super(config);
    this.slides = [];
    this.currentSlide = 0;
    this.container = null;
    this.autoplayInterval = null;

    // Inicializa Strapi Service se API URL estiver configurada
    if (config.apiUrl) {
      this.strapiService = new StrapiService({
        baseUrl: config.apiUrl,
        apiToken: config.strapiToken,
        enableCache: config.enableCache !== false,
        cacheExpiry: config.cacheExpiry
      });
    }
  }

  /**
   * Inicializa o módulo Carousel
   */
  async init() {
    if (this.initialized) {
      console.warn('CarouselModule já foi inicializado');
      return;
    }

    try {
      await this.fetchData();
      this.initialized = true;
    } catch (error) {
      console.error('Erro ao inicializar CarouselModule:', error);
      throw error;
    }
  }

  /**
   * Busca dados do Strapi
   */
  async fetchData() {
    // Se items foi passado via config, usa diretamente (dados já processados)
    if (this.config.items && Array.isArray(this.config.items) && this.config.items.length > 0) {
      this.slides = this.config.items;
      return;
    }

    if (!this.config.apiUrl) {
      console.warn('API URL não configurada');
      this.slides = [];
      return;
    }

    if (!this.strapiService) {
      console.warn('StrapiService não inicializado');
      this.slides = [];
      return;
    }

    try {
      // Usa StrapiService para buscar banners
      const data = await this.strapiService.get('banners', {
        populate: 'image',
        sort: 'order:asc'
      });

      // Transforma dados usando transformer
      this.slides = transformBanner(data);

      if (!this.slides || this.slides.length === 0) {
        console.warn('Nenhum banner retornado do Strapi');
        this.slides = [];
      }
    } catch (error) {
      console.error('Erro ao buscar dados do Strapi:', error);
      this.slides = [];
    }
  }

  /**
   * Renderiza o carousel no container especificado
   */
  render(container) {
    if (!this.initialized) {
      throw new Error('Módulo não inicializado. Execute init() primeiro.');
    }

    if (typeof container === 'string') {
      this.container = document.querySelector(container);
    } else {
      this.container = container;
    }

    if (!this.container) {
      throw new Error('Container não encontrado');
    }

    this.container.innerHTML = this.generateHTML();
    this.attachEventListeners();
    this.startAutoplay();
  }

  /**
   * Gera HTML do carousel
   */
  generateHTML() {
    if (this.slides.length === 0) {
      return '<div class="mzk-carousel-empty">Nenhum slide disponível</div>';
    }

    return `
      <div class="mzk-carousel">
        <div class="mzk-carousel-track">
          ${this.slides.map((slide, index) => `
            <div class="mzk-carousel-slide" data-index="${index}" style="background-image: url('${slide.image}')">
              <div class="mzk-carousel-overlay"></div>
              <div class="mzk-carousel-content">
                <div class="mzk-carousel-text">
                  <h2 class="mzk-carousel-category">${slide.description || ''}</h2>
                  <p class="mzk-carousel-title">${slide.title}</p>
                </div>
                ${slide.link && slide.link !== '#' ? `
                <div class="mzk-carousel-action">
                  <a href="${slide.link}" class="mzk-carousel-button">
                    ${slide.buttonText || 'Saiba Mais'}
                  </a>
                </div>
                ` : ''}
              </div>
            </div>
          `).join('')}
        </div>
        
        ${this.slides.length > 1 ? `
          <button class="mzk-carousel-prev" aria-label="Anterior">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M25 30L15 20L25 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="mzk-carousel-next" aria-label="Próximo">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 10L25 20L15 30" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        ` : ''}
      </div>
    `;
  }

  /**
   * Anexa event listeners
   */
  attachEventListeners() {
    const prevBtn = this.container.querySelector('.mzk-carousel-prev');
    const nextBtn = this.container.querySelector('.mzk-carousel-next');
    const track = this.container.querySelector('.mzk-carousel-track');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => this.prev());
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.next());
    }

    // Recalcula largura ao redimensionar
    this.resizeHandler = () => {
      this.updateSlideWidth();
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Atualiza largura do slide
   */
  updateSlideWidth() {
    const track = this.container.querySelector('.mzk-carousel-track');
    if (track) {
      this.slideWidth = track.offsetWidth;
    }
  }

  /**
   * Vai para o próximo slide
   */
  next() {
    this.goToSlide((this.currentSlide + 1) % this.slides.length);
  }

  /**
   * Vai para o slide anterior
   */
  prev() {
    this.goToSlide((this.currentSlide - 1 + this.slides.length) % this.slides.length);
  }

  /**
   * Vai para um slide específico
   */
  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;

    const track = this.container.querySelector('.mzk-carousel-track');
    if (!track) return;

    this.currentSlide = index;

    // Atualiza largura antes de scrollar
    this.updateSlideWidth();

    // Scroll suave para o slide
    track.scrollTo({
      left: this.slideWidth * index,
      behavior: 'smooth'
    });
  }

  /**
   * Inicia autoplay
   */
  startAutoplay(interval = 5000) {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => this.next(), interval);
  }

  /**
   * Para autoplay
   */
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  /**
   * Destrói o módulo e limpa recursos
   */
  destroy() {
    this.stopAutoplay();

    // Remove event listener de resize
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    if (this.container) {
      this.container.innerHTML = '';
    }

    this.slides = [];
    this.currentSlide = 0;
    this.container = null;
    this.slideWidth = 0;

    super.destroy();
  }

  /**
   * Retorna CSS do módulo
   */
  static getStyles() {
    return `
      .mzk-carousel {
        position: relative;
        width: 100%;
        overflow: hidden;
      }

      .mzk-carousel-track {
        display: flex;
        overflow-x: hidden;
        scroll-snap-type: x mandatory;
        width: 100%;
        scroll-behavior: smooth;
        transition: transform 0.5s ease-in-out;
      }

      .mzk-carousel-slide {
        width: 100%;
        height: 500px;
        flex-shrink: 0;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        background-size: cover;
        background-position: center;
        border-radius: 12px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        scroll-snap-align: start;
        overflow: hidden;
      }

      .mzk-carousel-overlay {
        position: absolute;
        inset: 0;
        background-color: rgba(0, 0, 0, 0.3);
        border-radius: 12px;
        width: 100%;
        height: 100%;
        left: 0;
        top: 0;
      }

      .mzk-carousel-content {
        position: relative;
        z-index: 10;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        padding: 60px;
        color: white;
      }

      .mzk-carousel-text {
        max-width: 600px;
      }

      .mzk-carousel-category {
        font-size: 18px;
        font-weight: bold;
        margin: 0 0 20px 0;
      }

      .mzk-carousel-title {
        font-size: 24px;
        margin: 0;
        line-height: 1.4;
      }

      .mzk-carousel-button {
        display: block;
        text-align: center;
        background-color: #42210B;
        color: white;
        font-weight: bold;
        font-size: 18px;
        padding: 12px 64px;
        border-radius: 4px;
        text-decoration: none;
        transition: background-color 0.3s ease-in-out;
      }

      .mzk-carousel-button:hover {
        background-color: #5a2d0f;
      }

      .mzk-carousel-prev,
      .mzk-carousel-next {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: white;
        width: 40px;
        height: 40px;
        cursor: pointer;
        z-index: 20;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.5;
        transition: opacity 0.3s;
      }

      .mzk-carousel-prev:hover,
      .mzk-carousel-next:hover {
        opacity: 1;
      }

      .mzk-carousel-prev {
        left: 8px;
      }

      .mzk-carousel-next {
        right: 8px;
      }

      .mzk-carousel-empty {
        padding: 40px;
        text-align: center;
        color: #666;
      }

      /* Responsivo */
      @media (max-width: 1024px) {
        .mzk-carousel-content {
          flex-direction: column;
          align-items: flex-start;
          padding: 32px;
        }

        .mzk-carousel-action {
          margin-top: 20px;
          width: 100%;
        }

        .mzk-carousel-button {
          width: 100%;
          min-width: 100%;
        }
      }

      @media (max-width: 768px) {
        .mzk-carousel-slide {
          height: 400px;
        }

        .mzk-carousel-category {
          font-size: 16px;
        }

        .mzk-carousel-title {
          font-size: 20px;
        }

        .mzk-carousel-button {
          font-size: 16px;
          padding: 10px 32px;
        }
      }
    `;
  }
}

export default CarouselModule;
